import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  IconButton,
  Alert,
  CircularProgress,
  Box,
  TableContainer,
  Paper
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { timeTrialService } from '../../services/timeTrials';

const DriverMappingDialog = ({ open, onClose, trial }) => {
  const [drivers, setDrivers] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [mappings, setMappings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && trial) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, trial]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    const [driversResult, participantsResult, mappingsResult] = await Promise.all([
      timeTrialService.getTrialDrivers(trial.id),
      timeTrialService.getEventParticipants(trial.id),
      timeTrialService.getDriverMappings(trial.id)
    ]);

    if (driversResult.success) {
      setDrivers(driversResult.data);
    } else {
      setError(driversResult.error);
    }
    
    if (participantsResult.success) {
      setParticipants(participantsResult.data);
    } else {
      setError(participantsResult.error);
    }
    
    if (mappingsResult.success) {
      const mappingObj = {};
      mappingsResult.data.forEach(m => {
        mappingObj[m.driver_name] = m.user_id;
      });
      setMappings(mappingObj);
    } else {
      setError(mappingsResult.error);
    }
    
    setLoading(false);
  };

  const handleMapDriver = async (driverName, userId) => {
    if (!userId) {
      // If empty value, delete the mapping
      await handleUnmap(driverName);
      return;
    }
    
    const result = await timeTrialService.createDriverMapping(trial.id, driverName, userId);
    if (result.success) {
      setMappings({ ...mappings, [driverName]: userId });
    } else {
      setError(result.error);
    }
  };

  const handleUnmap = async (driverName) => {
    const result = await timeTrialService.deleteDriverMapping(trial.id, driverName);
    if (result.success) {
      const newMappings = { ...mappings };
      delete newMappings[driverName];
      setMappings(newMappings);
    } else {
      setError(result.error);
    }
  };

  const unmappedCount = drivers.filter(d => !mappings[d.driver_name]).length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon />
          Manage Participant Mappings
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            
            <Alert severity="info" sx={{ mb: 2 }}>
              Map ACC driver names to event participants. Unmapped drivers will be skipped when finalizing.
            </Alert>
            
            {unmappedCount > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {unmappedCount} driver(s) are not mapped and will not receive points.
              </Alert>
            )}

            {drivers.length === 0 ? (
              <Alert severity="info">
                No drivers found with lap times for this time trial.
              </Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Driver Name (ACC)</strong></TableCell>
                      <TableCell><strong>Laps</strong></TableCell>
                      <TableCell><strong>Best Time</strong></TableCell>
                      <TableCell><strong>Mapped To</strong></TableCell>
                      <TableCell><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {drivers.map((driver) => (
                      <TableRow key={driver.driver_name}>
                        <TableCell>{driver.driver_name}</TableCell>
                        <TableCell>{driver.lap_count}</TableCell>
                        <TableCell>
                          {timeTrialService.formatLapTime(driver.best_time)}
                        </TableCell>
                        <TableCell>
                          <Select
                            value={mappings[driver.driver_name] || ''}
                            onChange={(e) => handleMapDriver(driver.driver_name, e.target.value)}
                            displayEmpty
                            size="small"
                            fullWidth
                          >
                            <MenuItem value="">
                              <em>Not mapped</em>
                            </MenuItem>
                            {participants.map((p) => (
                              <MenuItem key={p.id} value={p.id}>
                                {p.username}
                              </MenuItem>
                            ))}
                          </Select>
                        </TableCell>
                        <TableCell>
                          {mappings[driver.driver_name] && (
                            <IconButton
                              size="small"
                              onClick={() => handleUnmap(driver.driver_name)}
                              title="Remove mapping"
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DriverMappingDialog;

